import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import withSportBetStake from 'modules/betslip/betslip-view/stake-input-hoc/index.js';
import StakeInputView from 'modules/stake-input/index.js';
import PotentialReturns from 'modules/betslip/betslip-view/potential-returns/index.js';
import BetErrors from 'modules/betslip/betslip-view/bet-components/bet-errors/index.js';

const StakeInput = withSportBetStake(StakeInputView);
const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const BetslipUtils = MojitoServices.Betslip.utils;
const { BettingHelper } = MojitoPresentation.Utils;
const { utils: BetsUtils, types: BetsTypes } = MojitoServices.Bets;
const { FormattedLabelWithPrice, FormattedLabelWithTotalOdds } =
    MojitoPresentation.Components.FormattedText;
const { FlexPane } = MojitoPresentation.Components;
const EventUtils = MojitoServices.SportsContent.Events.utils;

export default class LabeledBet extends UIViewImplementation {
    renderPayoutInfo(betStake, currencyCode, hasAccaBoost, suspended) {
        const { payout = 0, payoutTax = 0 } = betStake;
        return (
            <FlexPane
                config={this.config.betslipBet.returnsContainer}
                class="ta-LabeledBetReturnsContainer"
            >
                <PotentialReturns
                    potentialReturns={payout}
                    disabled={suspended}
                    currencyCode={currencyCode}
                    hasAccaBoost={hasAccaBoost}
                />
                {this.renderPotentialTax(payoutTax, currencyCode)}
            </FlexPane>
        );
    }

    renderPotentialTax(payoutTax, currencyCode) {
        if (!payoutTax) {
            return null;
        }

        return (
            <FormattedLabelWithPrice
                label={this.resolveString('$BETSLIP.POTENTIAL_TAX')}
                price={payoutTax}
                currency={currencyCode}
                config={this.config.potentialTax}
            />
        );
    }

    renderTotalOdds(bet, numberOfLines) {
        const { odds, baseOdds, priceType } = bet;
        return (
            <FormattedLabelWithTotalOdds
                odds={EventUtils.getDisplayedOdds(odds, priceType, this.l10n)}
                baseOdds={priceType !== BetsTypes.PRICE_TYPE.SP ? baseOdds : undefined}
                isTotalOddsApplicable={BetsUtils.isTotalOddsApplicable(bet, numberOfLines)}
                config={this.config.totalOdds}
            />
        );
    }

    renderStakeInput(betStake) {
        if (this.config.stakeInput.hide) {
            return null;
        }
        const { stake = 0, freebetStake = 0 } = betStake;
        return (
            <StakeInput
                config={this.config.stakeInput}
                betId={this.props.bet.id}
                stakeForLine={stake || freebetStake}
                disabled={this.props.suspended}
                showStake={this.props.showStake}
                isEachWayAvailable={this.props.isEachWayAvailable}
                isEachWayActivated={this.props.isEachWayActivated}
                isValid={!this.props.hasErrors}
            />
        );
    }

    renderErrors() {
        const { errors } = this.props.betStake;
        return <BetErrors config={this.config.betErrors} errors={errors} />;
    }

    render() {
        const {
            bet,
            suspended,
            betStake,
            currencyCode,
            isEachWayActivated,
            bankerBetCount,
            singleBetCount,
        } = this.props;
        const hasAccaBoost = BetslipUtils.betHasAccaBoost(bet);
        const numberOfLines = BetslipUtils.getNumberOfLines(bet, isEachWayActivated);

        const isSingleLineBBAcca =
            !!bet && bet.isBBAcca && (numberOfLines === 1 || bet.numberOfEWLines);
        const isPlural = bet?.numberOfLines > 1;
        const isTeaser = !!bet.teaserType;
        const betFold = BetsTypes.BET_TYPE_TO_INDEX[bet.betType];
        const name =
            bankerBetCount === 0
                ? BettingHelper.getTypeName(
                      bet.betType,
                      this.l10n,
                      isTeaser,
                      isSingleLineBBAcca,
                      isPlural,
                      betFold,
                      numberOfLines,
                      bet.numberOfEWLines
                  )
                : BettingHelper.getBankerFormula(
                      bet.betType,
                      this.l10n,
                      singleBetCount,
                      bankerBetCount,
                      isPlural,
                      betFold,
                      numberOfLines,
                      bet.numberOfEWLines
                  );

        if (!bet.isBBAcca && bet.betType === BetsTypes.BET_TYPE.SINGLE && numberOfLines === 1) {
            return null;
        }
        return (
            <div
                style={this.config.betslipBet.container}
                className={`ta-labeledBet ta-${bet.betType}`}
            >
                {this.renderErrors()}
                <FlexPane
                    config={this.config.betslipBet.betInfoContainer}
                    class="ta-LabeledBetInfoContainer"
                >
                    <div style={this.style.totalOddsContainer} className="ta-TotalOddsContainer">
                        <div
                            className="ta-betName"
                            style={suspended ? this.style.betTypeSuspended : this.style.betType}
                        >
                            {name}
                        </div>
                        {this.renderTotalOdds(bet, numberOfLines)}
                    </div>
                    <div
                        className="ta-MultiplesInfo"
                        style={
                            suspended
                                ? this.config.betslipBet.multiplesInfoSuspended
                                : this.config.betslipBet.multiplesInfo
                        }
                    >
                        {this.resolveAndFormatString('$BETSLIP.MULTIPLIER', numberOfLines)}
                    </div>
                    <FlexPane
                        config={this.config.betslipBet.stakeInfoContainer}
                        class="ta-LabeledBetStakeInfoContainer"
                    >
                        {this.renderStakeInput(betStake)}
                    </FlexPane>
                </FlexPane>
                {this.renderPayoutInfo(betStake, currencyCode, hasAccaBoost, suspended)}
            </div>
        );
    }
}

LabeledBet.getStyle = function (config, applicationMode, merge) {
    const betConfig = config.betslipBet;
    return {
        betType: merge(
            {
                flex: '1 1 auto',
            },
            betConfig.betType
        ),
        betTypeSuspended: merge(
            {
                flex: '1 1 auto',
            },
            betConfig.betTypeSuspended
        ),
        totalOddsContainer: {
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
        },
    };
};
